'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

import { usePermissions } from '@/hooks/usePermissions';
import ProtectedRoute from '@/components/admin/ProtectedRoute';
import { TagRequestModal } from '@/components/admin/TagRequestModal';
import { tagService } from '@/lib/tag-service';
import { Tag as TagType } from '@/types/tags';
import { useTagData } from '@/hooks/useTagData';
import { useProfileEditor } from '@/hooks/useProfileEditor';
import { useImageUpload } from '@/hooks/useImageUpload';
import { activityLogService } from '@/lib/activity-log-service';
import { profileService } from '@/lib/profile-service';
import { ImageUploadField } from '@/components/ui/ImageUploadField';

export default function ProfilePage() {
  const { user, userData, refreshUserData } = useAuth();
  const { role: roleChecks } = usePermissions();
  const [linkingTwitter, setLinkingTwitter] = useState(false);
  const [twitterLinkError, setTwitterLinkError] = useState<string | null>(null);
  const [twitterLinkSuccess, setTwitterLinkSuccess] = useState(false);
  const [showTagRequestModal, setShowTagRequestModal] = useState(false);
  const [allTags, setAllTags] = useState<TagType[]>([]);
  
  // Use profile editor hook with primitive params (prevents infinite loop)
  const {
    profileData,
    isEditing,
    saving,
    error,
    success,
    startEditing,
    cancelEditing,
    updateField,
    updateProfileImage,
    saveProfile,
    clearError,
  } = useProfileEditor({
    userId: user?.uid || '',
    displayName: userData?.displayName || '',
    role: userData?.role || 'writer',
    initialProfile: userData,
    onSaved: refreshUserData,
  });
  
  // Memoize the onSuccess callback to prevent infinite re-renders
  const handleImageUploadSuccess = useCallback(async (imageUrl: string) => {
    // Update local state
    updateProfileImage(imageUrl);
    
    // Persist to database
    if (user?.uid && userData) {
      const oldImageUrl = userData.profileImage || '';
      await profileService.updateUserProfile(user.uid, { profileImage: imageUrl });
      
      // Log activity
      await activityLogService.logActivity(
        'user_profile_updated',
        userData,
        'user',
        userData.id!,
        userData.displayName,
        {
          profileChanges: {
            profileImage: { old: oldImageUrl, new: imageUrl }
          }
        },
        'admin'
      );
      
      // Refresh auth context
      await refreshUserData();
    }
  // Only depend on IDs, not the full objects which change reference
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, userData?.id, updateProfileImage]);
  
  // Use image upload hook
  const imageUpload = useImageUpload({
    userId: user?.uid || '',
    isGuest: false,
    onSuccess: handleImageUploadSuccess,
  });

  const { getTag, prefetchTags } = useTagData();

  // Load tags for tag display
  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await tagService.getAllTags({ isActive: true });
        setAllTags(tags);
        
        // Prefetch user's assigned tags for faster display
        if (userData?.assignedTags && userData.assignedTags.length > 0) {
          await prefetchTags(userData.assignedTags);
        }
      } catch (error) {
        console.error('Error loading tags:', error);
      }
    };
    
    if (userData?.role === 'writer' || userData?.role === 'editor') {
      loadTags();
    }
  }, [userData, prefetchTags]);

  const formatDate = (date?: Date) => {
    if (!date) return 'Never';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Get tag name from slug - try cache first, then fallback to allTags
  const getTagName = (slug: string): string => {
    const cachedTag = getTag(slug);
    if (cachedTag) return cachedTag.name;
    
    const tag = allTags.find(t => t.slug === slug);
    return tag?.name || slug;
  };

  // Handle tag request modal submission
  const handleTagRequestSubmitted = async () => {
    await refreshUserData();
  };

  const handleLinkTwitter = async () => {
    if (!userData || !user) {
      clearError();
      return;
    }

    if (userData.authProvider === 'twitter' && userData.twitterId) {
      return;
    }

    setLinkingTwitter(true);
    setTwitterLinkError(null);
    setTwitterLinkSuccess(false);

    try {
      const { TwitterAuthProvider, linkWithPopup } = await import('firebase/auth');
      const provider = new TwitterAuthProvider();
      const result = await linkWithPopup(user, provider);
      const credential = TwitterAuthProvider.credentialFromResult(result);
      const providerData = result.user.providerData.find(
        (p) => p.providerId === 'twitter.com'
      );
      
      if (!providerData) {
        throw new Error('Failed to get Twitter data');
      }
      
      const twitterData = {
        twitterId: providerData.uid,
        twitterUsername: '',
        twitterDisplayName: providerData.displayName || userData.displayName,
        twitterProfileImageUrl: providerData.photoURL || '',
        email: result.user.email || undefined,
      };
      
      const { db } = await import('@/lib/firebase');
      const { doc, updateDoc } = await import('firebase/firestore');
      
      if (db && userData.id) {
        const userDocRef = doc(db, 'users', userData.id);
        await updateDoc(userDocRef, {
          authProvider: 'twitter',
          twitterId: twitterData.twitterId,
          twitterUsername: twitterData.twitterUsername,
          twitterDisplayName: twitterData.twitterDisplayName,
          twitterProfileImageUrl: twitterData.twitterProfileImageUrl || null,
          ...(twitterData.email && !userData.email && { email: twitterData.email }),
          ...(twitterData.twitterProfileImageUrl && 
              !userData.profileImage && 
              { profileImage: twitterData.twitterProfileImageUrl }),
        });
        
        setTwitterLinkSuccess(true);
        setTimeout(() => {
          refreshUserData();
          setTwitterLinkSuccess(false);
        }, 3000);
      }
    } catch (err: any) {
      console.error('Twitter linking error:', err);
      
      // Handle specific error cases
      if (err.code === 'auth/popup-closed-by-user') {
        setTwitterLinkError('Popup was closed. Please try again.');
      } else if (err.code === 'auth/popup-blocked') {
        setTwitterLinkError('Popup was blocked by your browser. Please allow popups and try again.');
      } else if (err.code === 'auth/credential-already-in-use') {
        setTwitterLinkError('This Twitter account is already linked to another user.');
      } else if (err.code === 'auth/provider-already-linked') {
        setTwitterLinkError('Your account is already linked to Twitter.');
      } else {
        setTwitterLinkError(err.message || 'Failed to link Twitter account. Please try again.');
      }
    } finally {
      setLinkingTwitter(false);
    }
  };

  // Combine errors from profile editor and image upload
  const displayError = error || imageUpload.error;

  return (
    <ProtectedRoute>
      <div className="p-6">
        {/* Page header */}
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Profile Management</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Customize your author profile and manage your account
              </p>
            </div>
            <div className="flex space-x-3">
              {!isEditing ? (
                <button
                  onClick={startEditing}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Profile
                </button>
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={cancelEditing}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-neutral-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:ring-offset-neutral-800 focus:ring-blue-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveProfile}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    {saving ? (
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    ) : (
                      <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Messages */}
        {userData?.authProvider === 'twitter' && !userData?.twitterUsername && (
          <div className="mb-6 rounded-md bg-yellow-50 border-2 border-yellow-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  Action Required: Add Your Twitter Handle
                </h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Your Twitter account is linked, but we need your @handle to display your profile correctly. 
                  {!isEditing && (
                    <button
                      onClick={startEditing}
                      className="ml-1 font-semibold underline hover:text-yellow-900"
                    >
                      Click here to add it now
                    </button>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {displayError && (
          <div className="mb-6 rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{displayError}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Profile Information - Main Column */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-brand-dark-lighter shadow-sm rounded-xl p-8 border border-gray-200 dark:border-neutral-700">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Author Profile</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    How readers will see you on Descout
                  </p>
                </div>
              </div>
              
              {isEditing ? (
                <div className="space-y-6">
                  {/* Profile Image */}
                  <ImageUploadField
                    imageUrl={profileData.profileImage}
                    displayName={profileData.displayName}
                    imageUpload={imageUpload}
                    buttonColorClass="focus:ring-blue-500"
                    helpText="JPEG, PNG up to 5MB"
                  />

                  {/* Display Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={profileData.displayName}
                      onChange={(e) => updateField('displayName', e.target.value)}
                      className="block w-full border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your display name"
                    />
                  </div>

                  {/* Bio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bio
                    </label>
                    <textarea
                      value={profileData.bio}
                      onChange={(e) => updateField('bio', e.target.value)}
                      rows={3}
                      className="block w-full border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Tell readers about yourself..."
                    />
                  </div>

                  {/* Homepage */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Homepage URL
                    </label>
                    <input
                      type="url"
                      value={profileData.homepage}
                      onChange={(e) => updateField('homepage', e.target.value)}
                      className="block w-full border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://yourwebsite.com"
                    />
                  </div>

                  {/* Twitter Username - Editable for all users */}
                  {userData?.authProvider === 'twitter' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Twitter Handle
                      </label>
                      <div className="flex">
                        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 dark:border-neutral-600 bg-gray-50 dark:bg-neutral-800 text-gray-500 dark:text-gray-400 text-sm">
                          @
                        </span>
                        <input
                          type="text"
                          value={profileData.twitterUsername || ''}
                          onChange={(e) => updateField('twitterUsername', e.target.value)}
                          disabled={!isEditing || (() => {
                            // Writers and editors have rate limit
                            if (userData.role === 'writer' || userData.role === 'editor') {
                              if (userData.twitterUsernameLastUpdatedAt) {
                                const daysSinceUpdate = (Date.now() - new Date(userData.twitterUsernameLastUpdatedAt).getTime()) / (1000 * 60 * 60 * 24);
                                return daysSinceUpdate < 7;
                              }
                            }
                            return false;
                          })()}
                          className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="YourTwitterHandle"
                        />
                      </div>
                      {(() => {
                        // Show rate limit info for writers/editors
                        if ((userData.role === 'writer' || userData.role === 'editor') && userData.twitterUsernameLastUpdatedAt) {
                          const lastUpdate = new Date(userData.twitterUsernameLastUpdatedAt);
                          const daysSinceUpdate = (Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24);
                          
                          if (daysSinceUpdate < 7) {
                            const nextUpdateDate = new Date(lastUpdate.getTime() + 7 * 24 * 60 * 60 * 1000);
                            return (
                              <div className="mt-2 flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md">
                                <svg className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <div className="text-xs text-amber-800 dark:text-amber-300">
                                  <p className="font-medium">Rate Limited</p>
                                  <p>You can update your Twitter handle once per week. Last updated on {lastUpdate.toLocaleDateString()}. Next update available on {nextUpdateDate.toLocaleDateString()}.</p>
                                </div>
                              </div>
                            );
                          } else {
                            return (
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Last updated: {lastUpdate.toLocaleDateString()}
                              </p>
                            );
                          }
                        } else if (userData.role === 'admin' || userData.role === 'owner') {
                          return (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Your Twitter handle (editable anytime as {userData.role}).
                            </p>
                          );
                        } else {
                          return (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Your Twitter handle (editable). Update this if you change your Twitter username or prefer different capitalization.
                            </p>
                          );
                        }
                      })()}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Twitter Handle (Optional)
                      </label>
                      <div className="flex">
                        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 dark:border-neutral-600 bg-gray-50 dark:bg-neutral-800 text-gray-500 dark:text-gray-400 text-sm">
                          @
                        </span>
                        <input
                          type="text"
                          value={profileData.twitterHandle || ''}
                          onChange={(e) => updateField('twitterHandle', e.target.value)}
                          className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                          placeholder="username"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Manual entry (unverified). Link your Twitter account below for verified authentication.
                      </p>
                    </div>
                  )}

                  {/* Masthead identity — admin/owner only.
                      This gate is UX, not security: the control is firestore.rules
                      selfWriteMastheadOk(), which rejects a non-admin self-write that sets or
                      changes any of these three fields regardless of what the client sends. */}
                  {(roleChecks.isAdmin() || roleChecks.isOwner()) && (
                    <div className="pt-6 border-t border-gray-200 dark:border-neutral-700">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Masthead identity</h3>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Appears on the byline of pieces you approve. Leave the name blank to stay off the
                        masthead — pieces you approve will simply carry no review claim.
                      </p>

                      <div className="mt-4 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Name
                          </label>
                          <input
                            type="text"
                            value={profileData.mastheadName || ''}
                            onChange={(e) => updateField('mastheadName', e.target.value)}
                            className="block w-full border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Lam Hoang"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Handle
                          </label>
                          <input
                            type="text"
                            value={profileData.mastheadHandle || ''}
                            onChange={(e) => updateField('mastheadHandle', e.target.value)}
                            className="block w-full border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            placeholder="@0xmal_"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Handle links to
                          </label>
                          <input
                            type="url"
                            value={profileData.mastheadUrl || ''}
                            onChange={(e) => updateField('mastheadUrl', e.target.value)}
                            className="block w-full border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            placeholder="https://x.com/0xmal_"
                          />
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Must be an http(s) URL — anything else is dropped and the handle renders unlinked.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Profile Header */}
                  <div className="flex items-start gap-6">
                    <div className="h-24 w-24 rounded-xl overflow-hidden bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 flex-shrink-0 ring-4 ring-gray-100 dark:ring-neutral-700">
                      {userData?.profileImage ? (
                        <img
                          src={userData.profileImage}
                          alt="Profile"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center">
                          <span className="text-gray-400 dark:text-gray-500 text-3xl font-bold">
                            {userData?.displayName?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{userData?.displayName}</h3>
                      {userData?.authProvider === 'twitter' && userData?.twitterUsername && (
                        <a
                          href={`https://x.com/${userData.twitterUsername}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium mb-2"
                        >
                          <svg className="w-4 h-4 text-[#1DA1F2]" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                          </svg>
                          @{userData.twitterUsername}
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      )}
                      <p className="text-sm text-gray-600 dark:text-gray-400">{user?.email}</p>
                    </div>
                  </div>

                  {/* Bio - Always visible */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-3">About</h4>
                    {userData?.bio ? (
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{userData.bio}</p>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 italic">No bio added yet. Click Edit Profile to add one.</p>
                    )}
                  </div>

                  {/* Homepage - Always show section */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-3">Links</h4>
                    {userData?.homepage ? (
                      <a
                        href={userData.homepage}
                        target="_blank"
                        rel="nofollow ugc noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                        {userData.homepage.replace(/^https?:\/\//,'')}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 italic">No homepage added yet.</p>
                    )}
                  </div>

                  {/* Tags - Show if writer/editor */}
                  {(userData?.role === 'writer' || userData?.role === 'editor') && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">Writing Topics</h4>
                        <button
                          onClick={() => setShowTagRequestModal(true)}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                        >
                          Request New Topics →
                        </button>
                      </div>
                      {userData?.assignedTags && userData.assignedTags.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {userData.assignedTags.map((slug) => (
                            <span
                              key={slug}
                              className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700"
                            >
                              {getTagName(slug)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400 italic">No topics assigned yet. Request topics to start writing.</p>
                      )}
                      {userData?.requestedTags && userData.requestedTags.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-neutral-700">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Pending requests:</p>
                          <div className="flex flex-wrap gap-2">
                            {userData.requestedTags.map((slug) => (
                              <span
                                key={slug}
                                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700"
                              >
                                {getTagName(slug)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Account Stats Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* Compact Account Info */}
            <div className="bg-gradient-to-br from-white to-gray-50 dark:from-slate-800 dark:to-slate-850 shadow-sm rounded-xl p-5 border border-gray-200 dark:border-neutral-700">
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Account</h3>
              </div>
              
              <dl className="space-y-3">
                <div className="flex items-center justify-between">
                  <dt className="text-xs text-gray-500 dark:text-gray-400">Role</dt>
                  <dd>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${
                      userData?.role === 'owner'
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        : userData?.role === 'admin' 
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                        : userData?.role === 'editor'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    }`}>
                      {userData?.role}
                    </span>
                  </dd>
                </div>
                
                <div className="flex items-center justify-between">
                  <dt className="text-xs text-gray-500 dark:text-gray-400">Status</dt>
                  <dd>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ${
                      userData?.isActive 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${userData?.isActive ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      {userData?.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>

            {/* Compact Stats */}
            <div className="bg-white dark:bg-brand-dark-lighter shadow-sm rounded-xl p-5 border border-gray-200 dark:border-neutral-700">
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Activity</h3>
              </div>
              
              <dl className="space-y-3 text-xs">
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 mb-1">Member Since</dt>
                  <dd className="text-gray-900 dark:text-white font-medium">{formatDate(userData?.createdAt)}</dd>
                </div>
                
                <div className="pt-3 border-t border-gray-100 dark:border-neutral-700">
                  <dt className="text-gray-500 dark:text-gray-400 mb-1">Last Login</dt>
                  <dd className="text-gray-900 dark:text-white font-medium">{formatDate(userData?.lastLoginAt)}</dd>
                </div>
              </dl>
            </div>

            {/* Quick Stats for Writers/Editors */}
            {(userData?.role === 'writer' || userData?.role === 'editor') && (
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/10 dark:to-purple-900/10 shadow-sm rounded-xl p-5 border border-blue-100 dark:border-blue-800/30">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <h3 className="text-xs font-semibold text-blue-900 dark:text-blue-300 uppercase tracking-wide">Writing</h3>
                </div>
                
                <dl className="space-y-3">
                  <div className="flex items-baseline justify-between">
                    <dt className="text-xs text-blue-700 dark:text-blue-400">Articles</dt>
                    <dd className="text-2xl font-bold text-blue-900 dark:text-blue-200">{userData?.articleCount || 0}</dd>
                  </div>
                  
                  <div className="flex items-baseline justify-between">
                    <dt className="text-xs text-blue-700 dark:text-blue-400">Topics</dt>
                    <dd className="text-2xl font-bold text-blue-900 dark:text-blue-200">{userData?.assignedTags?.length || 0}</dd>
                  </div>
                </dl>
              </div>
            )}

            {/* Authentication Card - Twitter Migration */}
            {userData?.authProvider !== 'twitter' && (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/10 dark:to-orange-900/10 shadow-sm rounded-xl p-5 border border-amber-200 dark:border-amber-800/30">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                  <h3 className="text-xs font-semibold text-amber-900 dark:text-amber-300 uppercase tracking-wide">Authentication</h3>
                </div>
                
                <div className="space-y-3">
                  {twitterLinkSuccess && (
                    <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-3 border border-green-200 dark:border-green-800">
                      <div className="flex items-start">
                        <svg className="h-5 w-5 text-green-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div className="ml-2">
                          <p className="text-sm font-medium text-green-800 dark:text-green-300">
                            Twitter account linked successfully!
                          </p>
                          <p className="mt-1 text-xs text-green-700 dark:text-green-400">
                            You can now sign in with Twitter.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {twitterLinkError && (
                    <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-3 border border-red-200 dark:border-red-800">
                      <div className="flex items-start">
                        <svg className="h-5 w-5 text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <div className="ml-2">
                          <p className="text-sm font-medium text-red-800 dark:text-red-300">
                            {twitterLinkError}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="text-xs text-amber-800 dark:text-amber-300">
                    <p className="mb-2">Current: <span className="font-semibold">Email/Password</span></p>
                    <p className="text-amber-700 dark:text-amber-400 leading-relaxed">
                      Link your Twitter account to sign in faster and keep your profile up to date.
                    </p>
                  </div>
                  
                  <button
                    onClick={handleLinkTwitter}
                    disabled={linkingTwitter}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#1DA1F2] hover:bg-[#1a8cd8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1DA1F2] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {linkingTwitter ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Linking...
                      </>
                    ) : (
                      <>
                        <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                        </svg>
                        Link Twitter Account
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tag Request Modal */}
        {userData && (
          <TagRequestModal
            isOpen={showTagRequestModal}
            onClose={() => setShowTagRequestModal(false)}
            userId={userData.id || ''}
            assignedTags={userData.assignedTags || []}
            requestedTags={userData.requestedTags || []}
            tagRequestedAt={userData.tagRequestedAt}
            onRequestSubmitted={handleTagRequestSubmitted}
          />
        )}

      </div>
    </ProtectedRoute>
  );
}
